import { useEffect, useMemo, useState } from 'react';
import { Button } from './ui/button';
import { AdminUserProfileFormBody } from './admin/AdminUserProfileFormBody';
import {
  buildAdminProfileDiff,
  emptyAdminProfileForm,
  userToAdminProfileForm,
  type AdminProfileFieldKey,
  type AdminProfileFormData,
} from './admin/adminProfileForm.utils';
import {
  useAdminUpdateUserProfile,
  type AdminUser,
} from '../api/extensions/useAdminOps';
import { notify } from '../utils/notifications';

interface EditUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  user: AdminUser | null;
}

/**
 * Row-level admin edit modal opened from the pencil icon on the Manage Users
 * list. Behaviour and layout mirror the inline editor on the user-details
 * page (ProfileTab): same field sections, same required-for-user `*`
 * indicators, and the same partial-update contract — admins can save any
 * single field without filling in the rest.
 *
 * Required indicators here are ADVISORY only. They reflect what the user
 * themselves must complete to finish their profile; they do not block admin
 * saves. The PUT only contains fields the admin actually changed
 * (`buildAdminProfileDiff`) so untouched fields are never clobbered.
 */
function EditUserModal({ isOpen, onClose, user }: EditUserModalProps) {
  const updateMutation = useAdminUpdateUserProfile();
  const [formData, setFormData] = useState<AdminProfileFormData>(emptyAdminProfileForm());

  // Lock background scroll while modal is open. Same html-level !important
  // dance as the rest of the modal stack — Radix injects scroll-lock styles
  // that we have to outrank.
  useEffect(() => {
    if (!isOpen) return;
    const html = document.documentElement;
    html.style.setProperty('overflow-y', 'hidden', 'important');
    return () => {
      html.style.removeProperty('overflow-y');
    };
  }, [isOpen]);

  // Hydrate the draft from `user` whenever the prop changes so reopening the
  // modal on a different user doesn't show stale values.
  useEffect(() => {
    if (user) {
      setFormData(userToAdminProfileForm(user));
    }
  }, [user]);

  const handleFieldChange = (field: AdminProfileFieldKey, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const original = useMemo(
    () => (user ? userToAdminProfileForm(user) : emptyAdminProfileForm()),
    [user],
  );
  const hasChanges = useMemo(
    () =>
      (Object.keys(formData) as AdminProfileFieldKey[]).some(
        (k) => formData[k] !== original[k],
      ),
    [formData, original],
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;
    const diff = buildAdminProfileDiff(original, formData);
    if (Object.keys(diff).length === 0) {
      onClose();
      return;
    }
    try {
      await updateMutation.mutateAsync({ userId: user.id, data: diff });
      notify.success('Profile updated');
      onClose();
    } catch (err: any) {
      notify.error(err?.message || 'Failed to update user');
    }
  };

  if (!isOpen || !user) return null;

  const displayName =
    [user.first_name, user.last_name].filter(Boolean).join(' ') ||
    user.primary_email ||
    '';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/50 transition-opacity" onClick={onClose} />

      <div className="relative z-50 w-full max-w-3xl rounded-lg border bg-card text-card-foreground shadow-xl">
        <div className="p-6 border-b border-border">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold">Edit User</h2>
              <p className="mt-1 text-sm text-muted-foreground">
                Editing: <span className="font-semibold text-primary">{displayName}</span>
              </p>
              <p className="mt-1 text-xs text-muted-foreground">
                Fields marked <span className="text-destructive">*</span> are
                required when this user completes their own profile. Admins
                can save any single field independently.
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-muted-foreground hover:text-foreground transition-colors"
              aria-label="Close"
            >
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-4 max-h-[80vh] overflow-y-auto">
          <AdminUserProfileFormBody
            formData={formData}
            isEditing={true}
            onFieldChange={handleFieldChange}
          />

          <div className="mt-6 flex justify-end space-x-3">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={updateMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={updateMutation.isPending || !hasChanges}
            >
              {updateMutation.isPending ? 'Saving…' : 'Save Changes'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default EditUserModal;
